<template>
    <div class="wt-container">
        <div class="row">
            <div class="col-sm-12">
                <p class="introduction-text">
                    This graph shows the runoff, blue water availability and blue water footprint in the whole {{basinName}} basin. The runoff numbers represent the monthly natural discharge at the mouth of the river.
                </p>
                <CountryOrBasinSelector/>
                <div class="hydro-ct">
                    <div class="legend-ct">
                        <h5>Explanation of Colours</h5>
                        <p>Share of the natural runoff not used by human activities:</p>
                        <div class="color-box">
                            <div class="color color1"></div>
                        </div>
                        <p>Share of blue water footprint within environmental flow requirements:</p>
                        <div class="color-box">
                            <div class="color color2"></div>
                            <span class="color-text">(BWS &lt; 1)</span>
                        </div>
                        <p>Share of blue water footprint beyond environmental flow requirements:</p>
                        <div class="color-box">
                            <div class="color color3"></div>
                            <span class="color-text">(BWS &lt; 4)</span>
                        </div>
                        <div class="color-box">
                            <div class="color color4"></div>
                            <span class="color-text">(BWS &gt; 2)</span>
                        </div>
                        <div class="color-box">
                            <div class="color color5"></div>
                            <span class="color-text">(BWS &gt; 1.5)</span>
                        </div>
                        <div class="color-box">
                            <div class="color color6"></div><span class="color-text">(BWS &gt; 1)</span>
                        </div>
                        <p>Share of blue water footprint beyond the natural runoff:</p>
                        <div class="color-box">
                            <div class="color color7"></div>
                            <span class="color-text">(BWS &gt; 5)</span>
                        </div>
                    </div>
                    <HydroChart id="hydrograph" :structure="chartStructure" :tooltipHeight="260" :tooltipWidth="200"></HydroChart>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import HydroChart from '@/components/shared/Hydrograph.vue'
import CountryOrBasinSelector from "@/components/shared/CountryOrBasinSelector.vue"
import { basins } from '@/services/entity-model.js'
import { assessment } from "@/services/assessment-model.js"
import * as _ from 'lodash';

export default {
    name: 'HydroCt',
    components: {
        HydroChart,
        CountryOrBasinSelector
    },
    computed: {
        basinName() {
            if (assessment.countriesOrBasins.length == 1) {
                return assessment.countriesOrBasins[0].name;
            }
            return "river";
        },
        chartData() {
            const basinID = assessment.countriesOrBasins[0].basinID;
            const data = { blueFootprints: [], blueAvailabilities: [] }
            const basin = _.find(basins, { basinID:  basinID });
            data.blueFootprints = _.map(basin.blueFootprints.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            data.blueAvailabilities = _.map(basin.blueAvailabilities.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            return data;
        },
        chartStructure() {
            const basinID = assessment.countriesOrBasins[0].basinID;
            const data = { lines: {}, areas: [], columns: [] }
            const basin = _.find(basins, { basinID:  basinID });
            const blueFootprints = _.map(basin.blueFootprints.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            const blueAvailabilities = _.map(basin.blueAvailabilities.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            data.lines = {
                blueAvailabilities5: {data: _.map(blueAvailabilities, e => e * 5), color: 'blue', stroke: 2, label: "Natural Runoff"},
                blueAvailabilities: {data: blueAvailabilities, color: 'blue', stroke: 2, label: "Blue Water Availability"},
                blueFootprints: {data: blueFootprints, color: 'black', stroke: 2, label: "Blue Water Footprint"},
                blueAvailabilities4: {data: _.map(blueAvailabilities, e => e * 4), color: '', stroke: 2},
                blueAvailabilities2: {data: _.map(blueAvailabilities, e => e * 2), color: '', stroke: 2},
                blueAvailabilities15: {data: _.map(blueAvailabilities, e => e * 1.5), color: '', stroke: 2},
                floor: {data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], color: '', stroke: 2},
            };

            data.areas = [
                {top: 'blueFootprints', bottom: 'blueAvailabilities5', color: '#B0D9FF'},
                {top: 'floor', bottom: 'blueFootprints', color: '#A4BB06'},
                {top: 'blueAvailabilities', bottom: 'blueFootprints', color: '#EEEE00'},
                {top: 'blueAvailabilities15', bottom: 'blueFootprints', color: '#E89F00'},
                {top: 'blueAvailabilities2', bottom: 'blueFootprints', color: '#E34F00'},
                {top: 'blueAvailabilities4', bottom: 'blueFootprints', color: '#DD0000'},
                {top: 'blueAvailabilities5', bottom: 'blueFootprints', color: 'black'}
            ];

            data.columns = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            return data;
        }


    }
}
</script>

<style lang="scss" scoped>
.hydro-ct {
    position: relative;
    padding-left: 170px;
    .legend-ct {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        padding: 5px;
        background-color: rgba(237, 235, 229, 0.5);
        border: 1px solid black;
        border-radius: 5px;
        width: 170px;
        h5 {
            font-size: 14px;
            font-weight: 700; 
        }
        p {
            font-size: 12px;
            margin-bottom: 5px;
        }

        .color-box {
            display: flex;
            margin-bottom: 10px;
            & .color {
                width: 80px;
                height: 20px;
            }
            & .color1 {
                background-color: #b0d9ff;
            }
            & .color2 {
                background-color: #a4bb06;
            }
            & .color3 {
                background-color: #dd0000;
            }
            & .color4 {
                background-color: #e34f00;
            }
            & .color5 {
                background-color: #e89f00;
            }
            & .color6 {
                  background-color: #eeee00;
            }
            & .color7 {
                background-color: #000000;
            }
        }
        .color-text {
            padding-left: 5px;
            font-size: 12px;
            display: inline-block;
        }
    }
    
}
</style>
